import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { dashboardAPI, attendanceAPI, leaveAPI, notificationAPI } from "../../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { formatTime, formatDate } from "../../lib/utils";
import {
  Clock,
  CalendarDays,
  Bell,
  FileText,
  LogIn,
  LogOut,
  ArrowRight,
  Loader2,
} from "lucide-react";

const EmployeeDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [punchLoading, setPunchLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, attendanceRes, balanceRes, notifRes] = await Promise.all([
        dashboardAPI.getStats(),
        attendanceAPI.getToday(),
        leaveAPI.getBalance(),
        notificationAPI.getAll({ unread_only: true }),
      ]);
      setStats(statsRes.data);
      setTodayAttendance(attendanceRes.data.message ? null : attendanceRes.data);
      setLeaveBalance(balanceRes.data);
      setNotifications(notifRes.data.slice(0, 3));
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePunchIn = async () => {
    setPunchLoading(true);
    try {
      await attendanceAPI.punchIn({});
      toast.success("Punched in successfully!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to punch in");
    } finally {
      setPunchLoading(false);
    }
  };

  const handlePunchOut = async () => {
    setPunchLoading(true);
    try {
      await attendanceAPI.punchOut({});
      toast.success("Punched out successfully!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to punch out");
    } finally {
      setPunchLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const isPunchedIn = stats?.punched_in;
  const isPunchedOut = stats?.punched_out;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="employee-dashboard">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Welcome back, {user?.first_name}!
          </h1>
          <p className="text-slate-500 mt-1">
            Here's what's happening today
          </p>
        </div>

        {/* Punch Button */}
        <div className="flex items-center gap-4">
          {!isPunchedIn ? (
            <Button
              className="btn-primary h-14 px-8 text-lg relative overflow-hidden"
              onClick={handlePunchIn}
              disabled={punchLoading}
              data-testid="punch-in-btn"
            >
              {punchLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <LogIn className="w-5 h-5 mr-2" />
                  Punch In
                </>
              )}
            </Button>
          ) : !isPunchedOut ? (
            <Button
              variant="outline"
              className="h-14 px-8 text-lg border-2 border-red-200 text-red-600 hover:bg-red-50"
              onClick={handlePunchOut}
              disabled={punchLoading}
              data-testid="punch-out-btn"
            >
              {punchLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <LogOut className="w-5 h-5 mr-2" />
                  Punch Out
                </>
              )}
            </Button>
          ) : (
            <div className="text-center px-6 py-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-emerald-700 font-medium">Day Complete</p>
              <p className="text-sm text-emerald-600">
                {todayAttendance?.working_hours?.toFixed(1) || 0} hours worked
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Today's Attendance Card */}
      {todayAttendance && (
        <Card className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Today's Attendance</p>
                <p className="text-2xl font-bold mt-1">
                  {formatDate(todayAttendance.date)}
                </p>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-6">
                  <div>
                    <p className="text-white/80 text-sm">Punch In</p>
                    <p className="text-lg font-semibold">
                      {formatTime(todayAttendance.punch_in)}
                    </p>
                  </div>
                  <div>
                    <p className="text-white/80 text-sm">Punch Out</p>
                    <p className="text-lg font-semibold">
                      {todayAttendance.punch_out ? formatTime(todayAttendance.punch_out) : "--:--"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="stat-card card-hover">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Leaves</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {stats?.pending_leaves || 0}
                </p>
              </div>
              <div className="stat-card-icon bg-amber-100 text-amber-600">
                <CalendarDays className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card card-hover">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Unread Notifications</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {stats?.unread_notifications || 0}
                </p>
              </div>
              <div className="stat-card-icon bg-red-100 text-red-600">
                <Bell className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        {leaveBalance.slice(0, 2).map((balance, index) => (
          <Card key={index} className="stat-card card-hover">
            <CardContent className="p-6">
              <div>
                <p className="text-sm text-slate-500 capitalize">{balance.leave_type} Leave</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {balance.available}/{balance.annual_quota}
                </p>
                <p className="text-xs text-slate-400 mt-1">Available days</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions and Notifications */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <Link to="/leaves">
                <Button variant="outline" className="w-full h-20 flex-col gap-2">
                  <CalendarDays className="w-5 h-5" />
                  <span>Apply Leave</span>
                </Button>
              </Link>
              <Link to="/attendance">
                <Button variant="outline" className="w-full h-20 flex-col gap-2">
                  <Clock className="w-5 h-5" />
                  <span>Attendance History</span>
                </Button>
              </Link>
              <Link to="/salary/slips">
                <Button variant="outline" className="w-full h-20 flex-col gap-2">
                  <FileText className="w-5 h-5" />
                  <span>Salary Slips</span>
                </Button>
              </Link>
              <Link to="/documents">
                <Button variant="outline" className="w-full h-20 flex-col gap-2">
                  <FileText className="w-5 h-5" />
                  <span>My Documents</span>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Recent Notifications */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Recent Notifications</CardTitle>
            <Link to="/notifications">
              <Button variant="ghost" size="sm">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {notifications.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No new notifications</p>
              ) : (
                notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className="flex items-start gap-3 p-3 rounded-lg bg-slate-50"
                  >
                    <div className="w-2 h-2 mt-2 rounded-full bg-indigo-600" />
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{notif.title}</p>
                      <p className="text-sm text-slate-500 line-clamp-1">{notif.message}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leave Balance Summary */}
      {leaveBalance.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Leave Balance</CardTitle>
            <Link to="/leaves/balance">
              <Button variant="ghost" size="sm">
                View Details <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {leaveBalance.map((balance, index) => (
                <div key={index} className="p-4 rounded-lg bg-slate-50 text-center">
                  <p className="text-sm text-slate-500 capitalize">{balance.leave_type}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {balance.available}
                  </p>
                  <p className="text-xs text-slate-400">of {balance.annual_quota} days</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EmployeeDashboard;
